import React, { useState, useEffect, useMemo } from "react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import { Link, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"

import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "../../components/Common/DataTableContainer"

import { useDispatch, useSelector } from "react-redux"
import { fetchDriversData } from "store/drivers/actions"
import moment from "moment"
import SelectLitres from "./selectLitres"
import { toast } from "react-toastify"

const FuelReward = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [showSelectLitres, setShowSelectLitres] = useState(false)
  const [selectedDriver, setSelectedDriver] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState(null)

  const { data, loading, error } = useSelector(state => state.Drivers)

  useEffect(() => {
    dispatch(fetchDriversData({ limit: 100 }))
  }, [dispatch, (!showSelectLitres ? showSelectLitres : "")])

  const handleSearch = async (value) => {
    setSearchTerm(value)
    if (!value || value.trim() === "") {
      setSearchResults(null)
      return
    }
    setSearching(true)
    try {
      const token = localStorage.getItem("authToken")
      const response = await fetch(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/drivers?limit=100&search=${encodeURIComponent(value)}`,
        { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } }
      )
      const json = await response.json()
      if (response.ok) setSearchResults(json.data || [])
    } catch (e) {
      console.error(e)
    } finally {
      setSearching(false)
    }
  }

  const handleReset = async driverData => {
    if ( driverData.total_fuel_since_last_reward < 1 ) {
      return toast.error("Insufficient amount to reset")
    }
    Swal.fire({
      title: "Are you sure you want to reset?",
      text: "This driver’s counters are normally reset automatically after each diesel claim. Use manual reset only in case of a system or data error.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1DB954",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reset it!",
    }).then(async result => {
      if (result.isConfirmed) {
        setSelectedDriver(driverData)
        setShowSelectLitres(true)
      }
    })
  }

  const columns = useMemo(
    () => [
      {
        header: "Driver Id",
        accessorKey: "user_id",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Full Name",
        accessorKey: "full_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: (
          <>
            Vehicle <br /> (Licnese Plate)
          </>
        ),
        accessorKey: "vehicle",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: (
          <>
            Distance Traveled <br /> (since last reset)
          </>
        ),
        accessorKey: "distance_traveled",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: (
          <>
            Diesel Rewarded <br /> (since last reset)
          </>
        ),
        accessorKey: "diesel_rewarded",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Last Trip (Date/Time)",
        accessorKey: "last_trip",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const status = row.original.status?.toLowerCase()
          const active = status == "online"
          return (
            <span className={`badge ${active ? "bg-success" : "bg-danger"}`}>
              {status
                ? status.charAt(0).toUpperCase() + status.slice(1)
                : "Expired"}
            </span>
          )
        },
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => {
          const driverId = row.original?.user_id
          const driverData = row.original
          return (
            <div>
              <button
                className="btn btn-sm btn-success mx-2"
                onClick={() => navigate(`/drivers/${driverId}`)}
              >
                <i className="mdi mdi-eye"></i> View
              </button>
              <button
                className="btn btn-sm btn-warning"
                onClick={() => handleReset(driverData)}
              >
                <i className="mdi mdi-restore "></i> Reset
              </button>
            </div>
          )
        },
      },
    ],
    []
  )

  const totalRecords = (searchResults !== null ? searchResults : data?.data)?.length ?? 0

  const sourceData = searchResults !== null ? searchResults : data?.data

  const transformedData = useMemo(() => {
    return sourceData?.map(item => {
      return {
        ...item,
        full_name: item.user_name || "N/A",
        vehicle: item?.vehicles?.license_plate || "N/A",
        distance_traveled: item?.total_kms_since_last_reward + " Km" ?? "N/A",
        diesel_rewarded: item?.total_fuel_since_last_reward + " L" ?? "N/A",
        last_trip: item?.last_trip
          ? moment(item?.last_trip?.created_at).format("DD/MM/YY, hh:mm A")
          : "N/A",
      }
    })
  }, [data, searchResults])

  document.title = "Fuel Reward | Moeen"

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Fuel Rewards" />
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <>
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder={`Looking for something? ${totalRecords} results loaded.`}
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
            />
            {searching && <small className="text-muted">Searching...</small>}
          </div>
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={false}
            isPagination={true}
            pagination="pagination"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
          </>
        )}
      </div>
      {showSelectLitres && (
        <SelectLitres
          onClose={() => setShowSelectLitres(false)}
          driverData={selectedDriver}
        />
      )}
    </div>
  )
}

export default FuelReward
